import { useState, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { NativeSelect as Select } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { JsonEditor, validateJson } from '../ui/json-editor';
import { AgentType } from '../../types/agent';
import { useAgentTools } from '../../hooks/useTools';
import { useIntegrations } from '../../hooks/useIntegrations';
import { Link } from 'react-router-dom';

/**
 * Which per-user integration each tool needs before it can actually run.
 * Verified against services/tools/*.py. Ticking a tool whose integration isn't
 * connected produces an agent that fails at run time with no explanation — so the
 * builder shows the status and links straight to where you connect it.
 */
const TOOL_INTEGRATION: Record<string, { kind: string; label: string }> = {
  telegram_message: { kind: 'telegram', label: 'Telegram' },
  slack_message: { kind: 'slack', label: 'Slack' },
  discord_message: { kind: 'discord', label: 'Discord' },
  google_chat_message: { kind: 'google_chat', label: 'Google Chat' },
  notion_create_page: { kind: 'notion', label: 'Notion' },
  send_webhook: { kind: 'webhook', label: 'Outbound webhook' },
  google_calendar_list_events: { kind: 'google_calendar', label: 'Google Calendar' },
  google_calendar_create_event: { kind: 'google_calendar', label: 'Google Calendar' },
};

/** Pull the `tools` array out of a config JSON string (for edit mode). */
function parseTools(configStr?: string): string[] {
  if (!configStr) return [];
  try {
    const c = JSON.parse(configStr);
    return Array.isArray(c?.tools) ? c.tools.filter((t: unknown) => typeof t === 'string') : [];
  } catch {
    return [];
  }
}

/** Merge selected tool names into a config JSON string. */
function parseJsonOutput(configStr?: string): boolean {
  if (!configStr) return false;
  try {
    return JSON.parse(configStr)?.response_format === 'json';
  } catch {
    return false;
  }
}

/** Merge the tool selection + structured-output flag into the config JSON. */
function mergeExtras(
  configStr: string | undefined,
  tools: string[],
  jsonOutput: boolean
): string {
  let obj: Record<string, unknown> = {};
  if (configStr && configStr.trim()) {
    try {
      obj = JSON.parse(configStr);
    } catch {
      obj = {};
    }
  }
  if (tools.length > 0) obj.tools = tools;
  else delete obj.tools;
  if (jsonOutput) obj.response_format = 'json';
  else if (obj.response_format === 'json') delete obj.response_format;
  return Object.keys(obj).length > 0 ? JSON.stringify(obj, null, 2) : '';
}

// Custom Zod refinement for JSON validation
const jsonStringSchema = z
  .string()
  .optional()
  .refine(
    (val) => {
      if (!val || val.trim() === '') return true;
      return validateJson(val).isValid;
    },
    { message: 'Invalid JSON format' }
  );

// Zod schema for agent form validation
export const agentFormSchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .max(100, 'Name must be 100 characters or less'),
  description: z
    .string()
    .max(500, 'Description must be 500 characters or less')
    .optional()
    .or(z.literal('')),
  type: z.enum([
    'CUSTOMER_SUPPORT',
    'DATA_PROCESSING',
    'WORKFLOW_AUTOMATION',
    'CONTENT_GENERATION',
    'ANALYSIS',
    'INTEGRATION',
    'CUSTOM',
  ] as const),
  configuration: jsonStringSchema,
  tags: z.string().optional(),
});

export type AgentFormData = z.infer<typeof agentFormSchema>;

interface AgentFormProps {
  defaultValues?: Partial<AgentFormData>;
  onSubmit: (data: AgentFormData) => void | Promise<void>;
  isSubmitting?: boolean;
  submitLabel?: string;
}

const AGENT_TYPES: { value: AgentType; label: string }[] = [
  { value: 'CUSTOMER_SUPPORT', label: 'Customer Support' },
  { value: 'DATA_PROCESSING', label: 'Data Processing' },
  { value: 'WORKFLOW_AUTOMATION', label: 'Workflow Automation' },
  { value: 'CONTENT_GENERATION', label: 'Content Generation' },
  { value: 'ANALYSIS', label: 'Analysis' },
  { value: 'INTEGRATION', label: 'Integration' },
  { value: 'CUSTOM', label: 'Custom' },
];

export function AgentForm({
  defaultValues,
  onSubmit,
  isSubmitting = false,
  submitLabel = 'Save Agent',
}: AgentFormProps) {
  const [configJsonValid, setConfigJsonValid] = useState(true);
  const { data: availableTools } = useAgentTools();
  const { data: integrations } = useIntegrations();
  const [selectedTools, setSelectedTools] = useState<string[]>(() =>
    parseTools(defaultValues?.configuration)
  );
  const [jsonOutput, setJsonOutput] = useState<boolean>(() =>
    parseJsonOutput(defaultValues?.configuration)
  );

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<AgentFormData>({
    resolver: zodResolver(agentFormSchema),
    defaultValues: {
      name: '',
      description: '',
      type: 'CUSTOMER_SUPPORT',
      configuration: '',
      tags: '',
      ...defaultValues,
    },
  });

  const handleConfigValidationChange = useCallback((isValid: boolean) => {
    setConfigJsonValid(isValid);
  }, []);

  // Merge the tool checkboxes into the config JSON before submitting.
  const submitWithTools = (data: AgentFormData) =>
    onSubmit({
      ...data,
      configuration: mergeExtras(data.configuration, selectedTools, jsonOutput),
    });

  return (
    <form onSubmit={handleSubmit(submitWithTools)} className="space-y-6" noValidate>
      <div className="space-y-2">
        <Label htmlFor="name">
          Agent Name <span className="text-red-500">*</span>
        </Label>
        <Input
          id="name"
          placeholder="e.g., Customer Support Bot"
          {...register('name')}
          disabled={isSubmitting}
          aria-invalid={!!errors.name}
          // aria-describedby + role="alert": the field was marked invalid but
          // the reason was never associated with it, so a screen reader
          // announced "invalid" and nothing else. Validation here is zod, not
          // the browser, so there is no native validationMessage to fall back on.
          aria-describedby={errors.name ? 'name-error' : undefined}
        />
        {errors.name && (
          <p id="name-error" role="alert" className="text-sm text-red-600">
            {errors.name.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="type">
          Agent Type <span className="text-red-500">*</span>
        </Label>
        <Select id="type" {...register('type')} disabled={isSubmitting}>
          {AGENT_TYPES.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </Select>
        {errors.type && (
          <p className="text-sm text-red-600">{errors.type.message}</p>
        )}
        <p className="text-xs text-muted-foreground">
          Select the type of agent you want to create
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          placeholder="Describe what this agent does..."
          rows={4}
          {...register('description')}
          disabled={isSubmitting}
          aria-invalid={!!errors.description}
          aria-describedby={errors.description ? 'description-error' : undefined}
        />
        {errors.description && (
          <p id="description-error" role="alert" className="text-sm text-red-600">
            {errors.description.message}
          </p>
        )}
        <p className="text-xs text-muted-foreground">
          Optional. Describe the agent's purpose and capabilities.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="tags">Tags</Label>
        <Input
          id="tags"
          placeholder="e.g., customer-service, chatbot, production"
          {...register('tags')}
          disabled={isSubmitting}
        />
        <p className="text-xs text-muted-foreground">
          Separate tags with commas
        </p>
      </div>

      {availableTools && availableTools.length > 0 && (
        <div className="space-y-2">
          <Label>Tools</Label>
          <div className="space-y-2 rounded-md border p-3">
            {availableTools.map((tool) => (
              <label
                key={tool.name}
                className="flex items-start gap-2 text-sm cursor-pointer"
              >
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={selectedTools.includes(tool.name)}
                  disabled={isSubmitting}
                  onChange={(e) =>
                    setSelectedTools((prev) =>
                      e.target.checked
                        ? [...prev, tool.name]
                        : prev.filter((t) => t !== tool.name)
                    )
                  }
                />
                <span>
                  <span className="font-mono font-medium">{tool.name}</span>
                  <span className="block text-xs text-muted-foreground">
                    {tool.description}
                  </span>
                  {(() => {
                    const req = TOOL_INTEGRATION[tool.name];
                    if (!req) return null;
                    const connected = (integrations ?? []).some(
                      (i) => i.kind === req.kind && i.configured
                    );
                    return connected ? (
                      <span className="mt-0.5 block text-xs font-medium text-emerald-700">
                        ✓ {req.label} connected
                      </span>
                    ) : (
                      <span className="mt-0.5 block text-xs font-medium text-amber-700">
                        ⚠ Needs {req.label} —{' '}
                        <Link
                          to="/account"
                          className="underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          connect it in Account → Integrations
                        </Link>
                      </span>
                    );
                  })()}
                </span>
              </label>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            Let this agent take real actions. Tools marked ⚠ need their integration
              connected first, or the agent will fail at run time.
          </p>
        </div>
      )}

      <div className="space-y-2">
        <label className="flex items-start gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            className="mt-1"
            checked={jsonOutput}
            disabled={isSubmitting}
            onChange={(e) => setJsonOutput(e.target.checked)}
          />
          <span>
            <span className="font-medium">Structured JSON output</span>
            <span className="block text-xs text-muted-foreground">
              The agent returns JSON. In a workflow, its fields pass to the next
              step and can be used in conditions.
            </span>
          </span>
        </label>
      </div>

      <div className="space-y-2">
        <Label htmlFor="configuration">Configuration (JSON)</Label>
        <Controller
          name="configuration"
          control={control}
          render={({ field }) => (
            <JsonEditor
              value={field.value || ''}
              onChange={field.onChange}
              height={250}
              disabled={isSubmitting}
              placeholder={'{\n  "key": "value"\n}'}
              error={errors.configuration?.message}
              onValidationChange={handleConfigValidationChange}
            />
          )}
        />
        <p className="text-xs text-muted-foreground">
          Optional. Define custom configuration for your agent in JSON format.
        </p>
      </div>

      <Button type="submit" disabled={isSubmitting || !configJsonValid} className="w-full">
        {isSubmitting ? (
          <span className="flex items-center gap-2">
            <LoadingSpinner size="sm" />
            Saving...
          </span>
        ) : (
          submitLabel
        )}
      </Button>
    </form>
  );
}

function LoadingSpinner({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-8 w-8',
  };

  return (
    <svg
      className={`animate-spin ${sizeClasses[size]}`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

export default AgentForm;
